import { BearerTokenGetterContext } from '@pelckmans/business-components/context/BearerTokenGetterContext';
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useExerciseManagerContext } from '../../contexts/ExerciseManagerContext';
import { ExercisePhase } from '../../@types/exercise-types';
import * as exerciseApi from '../../api/exercises-api';
import useAnalytics from '../../../../hooks/useAnalytics';
import { ANALYTICS_EVENT_TYPES, AnalyticsData } from '../../../../hooks/constants/Analytics';
import Dialog from '@pelckmans/elementis/components/dialog';
import { ExerciseTranslationsContext } from '../../contexts/ExerciseTranslationsContext';
import Button from '@pelckmans/elementis/components/button';
import PauseIcon from '@pelckmans/elementis/icons/pause';
import CloseIcon from '@pelckmans/elementis/icons/close';
import { MedialinkDialogHeaderTags } from '../../../../../../pages/material/components/MedialinkDialogHeaderTags';
import { ExerciseLoading } from '../exercise-loading';
import { ExerciseIframe } from '../exercise-iframe';
import ServiceNotAvailable from '../service-not-available';
import { ExerciseCalculating } from '../exercise-calculating';
import RetryAlert from '../retry-alert';

interface ActiveSessionModalProps {
  phaseAction: ExercisePhase | undefined;
  analyticsData?: AnalyticsData;
  setPhase: Dispatch<SetStateAction<'launch' | 'activity' | 'progression' | undefined>>;
  onSessionPaused: (session: exerciseApi.PauseExerciseSessionResponse) => void;
  onSessionFinished: (result: exerciseApi.StopExerciseSessionResponse) => void;
}

function ActiveSessionModal({
  phaseAction,
  analyticsData,
  setPhase,
  onSessionPaused,
  onSessionFinished,
}: ActiveSessionModalProps) {
  const getBearerToken = useContext(BearerTokenGetterContext);

  const translations = useContext(ExerciseTranslationsContext);
  const { assignmentId, module, language, studioGroup, medialink, onClose } = useExerciseManagerContext();
  const analytics = useAnalytics();

  const [url, setUrl] = useState<string>();
  const [state, setState] = useState<'loading' | 'play' | 'calculating-paused-score' | 'calculating-final-score'>(
    'loading',
  );
  const [serviceAvailable, setServiceAvailable] = useState<boolean>(true);
  const [retryAction, setRetryAction] = useState<() => Promise<void>>();

  const eventCaptured = useRef<boolean>(false);

  useEffect(() => {
    const play = async (): Promise<void> => {
      const token = await getBearerToken();

      let exerciseUrl;

      if (assignmentId) {
        if (phaseAction === 'start') {
          exerciseUrl = await exerciseApi.startAssignmentSession(
            token!,
            module.id,
            assignmentId,
            language,
            studioGroup,
          );
        }
        if (phaseAction === 'resume') {
          exerciseUrl = await exerciseApi.resumeAssignmentSession(token!, module.id, assignmentId, studioGroup);
        }
      } else {
        if (phaseAction === 'start') {
          exerciseUrl = await exerciseApi.startFreeExerciseSession(
            token!,
            module.id,
            medialink.id,
            language,
            studioGroup,
          );
        }
        if (phaseAction === 'resume') {
          exerciseUrl = await exerciseApi.resumeFreeExerciseSession(token!, module.id, medialink.id, studioGroup);
        }
      }

      if (exerciseUrl) setUrl(exerciseUrl);

      setState('play');
    };

    const run = async () => {
      if (state === 'loading') {
        try {
          await play();
        } catch (e) {
          setServiceAvailable(false);
        }
      }
    };

    run();
  }, [assignmentId, state, getBearerToken, language, module.id, studioGroup, phaseAction, medialink.id]);

  useEffect(() => {
    const captureEvent = async (): Promise<void> => {
      const token = await getBearerToken();

      if (analyticsData && token && !eventCaptured.current) {
        eventCaptured.current = true;
        analytics.capture(
          {
            eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
            objectId: medialink.id,
            analyticsData,
          },
          token,
        );
      }
    };

    captureEvent();
  }, [analytics, analyticsData, getBearerToken, medialink.id]);

  const handlePause = async (): Promise<void> => {
    setState('calculating-paused-score');
    setRetryAction(undefined);

    try {
      const token = await getBearerToken();

      const data = assignmentId
        ? await exerciseApi.pauseAssignmentSession(token!, module.id, assignmentId, studioGroup)
        : await exerciseApi.pauseFreeExerciseSession(token!, module.id, medialink.id, studioGroup);

      onSessionPaused(data);

      setPhase('progression');
    } catch (e) {
      setRetryAction(() => handlePause);
    }
  };

  const handleFinish = useCallback(async () => {
    setRetryAction(undefined);
    setState('calculating-final-score');

    try {
      const token = await getBearerToken();
      const data = assignmentId
        ? await exerciseApi.stopAssignmentSession(token!, module.id, assignmentId, studioGroup)
        : await exerciseApi.stopFreeExerciseSession(token!, module.id, medialink.id, studioGroup);

      onSessionFinished(data);

      setPhase('progression');
    } catch (e) {
      setRetryAction(() => handleFinish);
    }
  }, [getBearerToken, assignmentId, module.id, studioGroup, medialink.id, setPhase, onSessionFinished]);

  const dialogHeaderProps = () => {
    if (!serviceAvailable) {
      return {
        buttonGroupProps: {
          'aria-label': undefined,
          role: undefined,
          children: (
            <Button prominence="neutral" startIcon={<CloseIcon data-testid="close-icon" />} onClick={onClose}>
              {translations.closeDialog}
            </Button>
          ),
        },
      };
    }

    switch (state) {
      case 'loading':
      case 'calculating-final-score':
        return {
          buttonGroupProps: {
            'aria-label': undefined,
            role: undefined,
            children: (
              <Button
                prominence="neutral"
                startIcon={<CloseIcon data-testid="close-icon" />}
                onClick={onClose}
                busy={state === 'calculating-final-score' && !retryAction}
              >
                {translations.closeDialog}
              </Button>
            ),
          },
        };
      case 'play':
      case 'calculating-paused-score':
        return {
          buttonGroupProps: {
            'aria-label': undefined,
            role: undefined,
            children: (
              <Button
                prominence="neutral"
                startIcon={<PauseIcon data-testid="pause-icon" />}
                onClick={handlePause}
                busy={state === 'calculating-paused-score' && !retryAction}
              >
                {translations.activeSession.buttons.pause}
              </Button>
            ),
          },
        };
    }
  };

  return (
    <Dialog
      id="active-session-dialog"
      modal
      aria-label={translations.activeSession.a11yLabel}
      onClose={onClose}
      size="full"
    >
      <Dialog.Header title={medialink.name} {...dialogHeaderProps()} titleTooltip={medialink.name}>
        {/* should this component also be moved to elementis or should headerProps be used here? */}
        <MedialinkDialogHeaderTags material={medialink} module={module} />
      </Dialog.Header>
      <Dialog.Body>
        {!serviceAvailable && <ServiceNotAvailable />}

        {serviceAvailable && state === 'loading' && <ExerciseLoading />}

        {serviceAvailable && url && (state === 'play' || state === 'calculating-paused-score') && (
          <>
            {retryAction && (
              <RetryAlert
                onRetry={retryAction}
                message={translations.activeSession.alerts.retryAction.message}
                ctaText={translations.activeSession.alerts.retryAction.buttons.retry}
              />
            )}
            <ExerciseIframe exerciseUrl={url} medialink={medialink} onExerciseFinished={handleFinish} />
          </>
        )}

        {serviceAvailable && state === 'calculating-final-score' && (
          <>
            {retryAction && (
              <RetryAlert
                onRetry={retryAction}
                message={translations.activeSession.alerts.retryAction.message}
                ctaText={translations.activeSession.alerts.retryAction.buttons.retry}
              />
            )}
            <ExerciseCalculating />
          </>
        )}
      </Dialog.Body>
    </Dialog>
  );
}

export default ActiveSessionModal;
